import type { AssistantContextRef } from '~/data/api/client'
import type { ChargerModel } from '~/data/api/client/chargers'
import type { BatteryModel, GeneratorModel } from '~/data/api/client/ders'
import type { VehicleModel } from '~/data/api/client/fleet-management'

/**
 * Context ref as the UI builds and stores it.
 *
 * The generated {@link AssistantContextRef} leaves every field optional (OpenAPI has no
 * `required`); in practice we always have type/id/label when a tag is on screen.
 */
export type FocusedContextRef = Required<Pick<AssistantContextRef, 'type' | 'id' | 'label'>> &
  Pick<AssistantContextRef, 'name' | 'make' | 'model'>

/** Narrows an API/generated ref; returns null when type or id is missing. */
export function toFocusedContextRef(ref: AssistantContextRef): FocusedContextRef | null {
  if (!ref.type || !ref.id) return null
  return {
    ...ref,
    type: ref.type,
    id: ref.id,
    label: ref.label?.trim() || ref.name?.trim() || ref.id
  }
}

function displayName(name: string | undefined, fallbackId: string): string {
  const trimmed = name?.trim()
  return trimmed ? trimmed : fallbackId
}

export function buildChargerContextRef(charger: ChargerModel): FocusedContextRef {
  const name = displayName(charger.chargerName, charger.chargerId)
  return {
    type: 'charger',
    id: charger.chargerId,
    label: name,
    name,
    make: charger.manufacturerName,
    model: charger.makeModelName
  }
}

export function buildVehicleContextRef(vehicle: VehicleModel): FocusedContextRef {
  const name = displayName(vehicle.vehicleDisplayNm ?? vehicle.vehicleNm, vehicle.id)
  return {
    type: 'vehicle',
    id: vehicle.id,
    label: name,
    name,
    make: vehicle.make,
    model: vehicle.model
  }
}

export function buildBatteryContextRef(battery: BatteryModel): FocusedContextRef {
  const name = displayName(battery.name, battery.id)
  return {
    type: 'battery',
    id: battery.id,
    label: name,
    name,
    make: battery.manufacturer,
    model: battery.modelNumber
  }
}

export function buildGeneratorContextRef(generator: GeneratorModel): FocusedContextRef {
  const name = displayName(generator.name, generator.id)
  return {
    type: 'generator',
    id: generator.id,
    label: name,
    name,
    make: generator.manufacturer,
    model: generator.modelNumber
  }
}

export function buildSiteContextRef(site: { depotId: string; depotNm?: string }): FocusedContextRef {
  const name = displayName(site.depotNm, site.depotId)
  return { type: 'site', id: site.depotId, label: name, name }
}

export function buildUserContextRef(user: { id: string; name?: string }): FocusedContextRef {
  const name = displayName(user.name, user.id)
  return { type: 'user', id: user.id, label: name, name }
}

export function buildOrgContextRef(org: { id: string; organizationNm?: string }): FocusedContextRef {
  const name = displayName(org.organizationNm, org.id)
  return { type: 'org', id: org.id, label: name, name }
}

export function buildProductContextRef(product: { id: string; name?: string }): FocusedContextRef {
  const name = displayName(product.name, product.id)
  return { type: 'product', id: product.id, label: name, name }
}

export function buildAgreementContextRef(agreement: { id: string; name?: string }): FocusedContextRef {
  const name = displayName(agreement.name, agreement.id)
  return { type: 'agreement', id: agreement.id, label: name, name }
}

export function buildSynopLinkContextRef(link: {
  id: string
  nickName?: string
  thingName?: string
}): FocusedContextRef {
  const name = displayName(link.nickName ?? link.thingName, link.id)
  return { type: 'synoplink', id: link.id, label: name, name }
}
